import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope } from '../../../widgets/types';

type Attempt = {
  id: string;
  kind: 'lovable' | 'intent' | 'environment';
  name: string;
  project_id: string | null;
  action: 'discard' | 'manage';
  manage_url?: string;
  reason?: string;
};
type Preview = {
  ok: boolean;
  error?: string;
  items: Attempt[];
  errors: Array<{ source: string; error: string }>;
};

export function UnfinishedProjects({ scope }: { scope: EffectiveScope }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Cíl se zachytí s konkrétní položkou; přepnutí uzlu nepřenese rozběhnuté smazání jinam.
  const queryKey = ['toolbox-project-cleanup', scope.node, scope.identity];
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await callWidgetTool<Preview>('toolbox.project_cleanup', {}, scope);
      if (!result.ok) throw new Error(result.error);
      return result;
    },
    retry: false,
  });
  const discard = useMutation({
    mutationFn: async ({ item, target }: { item: Attempt; target: EffectiveScope }) => {
      const result = await callWidgetTool<{ ok: boolean; error?: string }>(
        'toolbox.project_cleanup',
        { action: 'discard', kind: item.kind, id: item.id },
        target,
      );
      if (!result.ok) throw new Error(result.error);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['toolbox-project-cleanup'] }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('toolbox.projectCleanup.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className='text-sm text-gray-400 mb-3'>{t('toolbox.projectCleanup.description')}</p>
        <Button
          size='sm'
          onClick={() => {
            discard.reset();
            void query.refetch();
          }}
          loading={query.isFetching}
        >
          {t('toolbox.projectCleanup.refresh')}
        </Button>
        {query.error && <p role='alert' className='text-red-400 mt-3'>{query.error.message}</p>}
        {query.data?.errors.map((error) => (
          <p key={error.source} role='alert' className='text-red-400 mt-3'>
            {error.source}: {error.error}
          </p>
        ))}
        {discard.error && <p role='alert' className='text-red-400 mt-3'>{discard.error.message}</p>}
        {discard.isSuccess && (
          <p role='status' className='text-green-400 mt-3'>{t('toolbox.projectCleanup.removed')}</p>
        )}
        {query.data?.items.length === 0 && query.data.errors.length === 0 && (
          <p className='text-sm text-gray-400 mt-3'>{t('toolbox.projectCleanup.empty')}</p>
        )}
        <div className='divide-y divide-gray-800 mt-3'>
          {query.data?.items.map((item) => (
            <div
              key={`${item.kind}:${item.id}`}
              className='py-3 flex flex-wrap items-center justify-between gap-3'
            >
              <div className='min-w-0'>
                <p className='font-medium break-all'>{item.name}</p>
                <p className='text-xs text-gray-400'>
                  {t(`toolbox.projectCleanup.${item.action === 'discard' ? 'unused' : 'realized'}`)}
                </p>
                <p className='text-xs text-gray-500 font-mono break-all'>
                  {item.project_id ?? item.id}
                </p>
                {item.reason && <p className='text-sm text-gray-400'>{item.reason}</p>}
              </div>
              {item.action === 'discard'
                ? (
                  <Button
                    size='sm'
                    variant='danger'
                    disabled={discard.isPending || query.isFetching}
                    onClick={() => discard.mutate({ item, target: { ...scope } })}
                  >
                    {t('toolbox.projectCleanup.discard')}
                  </Button>
                )
                : item.manage_url && (
                  <Link
                    className='text-sm text-blue-400 underline'
                    to={`${item.manage_url}${
                      scope.node ? `&node=${encodeURIComponent(scope.node)}` : ''
                    }`}
                  >
                    {t('toolbox.projectCleanup.manage')}
                  </Link>
                )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
